/**
 * Sleep — trend-first view of sleep sessions, with manual entry behind a
 * modal. Most data arrives via the Sleep Cycle CSV import; manual editing
 * is the exception.
 *
 * Layout (top to bottom):
 *   • Header + "+ Sleep" button (opens SleepFormModal for today).
 *   • Two charts side-by-side: Sleep duration (h) and Sleep score (0–100).
 *     Each chart has its own W/M/3M/Y/All range pills.
 *   • Recent nights list (clickable → opens the modal pre-filled for that
 *     date — same path the user uses to add new entries).
 *   • Recovery / readiness signals (agent-computed).
 */

import { useMemo, useState } from 'react';
import {
  useSleepSessions,
  useRecoverySignals,
  type SleepSessionDto,
} from '../../api/health';
import { SleepFormModal } from '../../components/SleepFormModal';
import { TimeSeriesChart, type ChartPoint } from '../../components/TimeSeriesChart';
import styles from './health.module.css';

export function HealthSleepScreen() {
  const sleep = useSleepSessions();
  const recovery = useRecoverySignals();

  // `null` means "open for today"; a specific date string means "edit that
  // night". One modal handles both flows.
  const [modalDate, setModalDate] = useState<string | null | undefined>(undefined);
  const modalOpen = modalDate !== undefined;

  const sessions = useMemo<SleepSessionDto[]>(
    () => sleep.data?.sessions ?? [],
    [sleep.data],
  );

  const durationPoints = useMemo<ChartPoint[]>(
    () => sessionsToPoints(sessions, (s) => s.totalH),
    [sessions],
  );
  const scorePoints = useMemo<ChartPoint[]>(
    () => sessionsToPoints(sessions, (s) => s.score),
    [sessions],
  );

  return (
    <div className={styles.page}>
      <header
        className={styles.head}
        style={{ alignItems: 'center', justifyContent: 'space-between' }}
      >
        <div>
          <h2 className={styles.title}>Sleep</h2>
          <div className={styles.subtitle}>
            Nightly totals and score over time. Manual entry sits behind
            the button — most data is imported (Sleep Cycle) or synced.
          </div>
        </div>
        <div className={styles.actions}>
          <button
            type="button"
            className="kos-btn kos-btn-primary"
            onClick={() => setModalDate(null)}
          >
            + Sleep
          </button>
        </div>
      </header>

      {/* Charts — two stacked on small screens, side-by-side from 720px+
          via the shared .grid utility. */}
      <div className={styles.grid} style={{ marginBottom: 18 }}>
        <TimeSeriesChart
          label="Sleep duration"
          unit="h"
          points={durationPoints}
          defaultRange="M"
        />
        <TimeSeriesChart
          label="Sleep score"
          unit=""
          points={scorePoints}
          defaultRange="M"
        />
      </div>

      <section style={{ marginBottom: 18 }}>
        <div className={styles.cardHead}>
          <h3 className={styles.cardTitle}>Recent nights</h3>
        </div>
        {sessions.length === 0 ? (
          <div className={styles.empty}>
            No sleep records yet. Import a Sleep Cycle CSV from Data loads
            or use "+ Sleep" to log one by hand.
          </div>
        ) : (
          <div className={styles.list}>
            {sessions.slice(0, 14).map((s) => (
              <button
                key={s.id}
                type="button"
                className={styles.row}
                onClick={() => setModalDate(s.date)}
                style={{
                  textAlign: 'left',
                  font: 'inherit',
                  cursor: 'pointer',
                  width: '100%',
                }}
                title={`Edit ${s.date}`}
              >
                <div className={styles.rowMain}>
                  <div className={styles.rowTitle}>{s.date}</div>
                  <div className={styles.rowMeta}>
                    {s.totalH ?? '—'}h total
                    {s.deepH ? ` · ${s.deepH}h deep` : ''}
                    {s.remH ? ` · ${s.remH}h REM` : ''}
                    {s.efficiency ? ` · ${s.efficiency}% efficiency` : ''}
                    {` · ${s.source}`}
                  </div>
                </div>
                {s.score !== null && (
                  <span className={styles.rowValue}>{s.score}</span>
                )}
              </button>
            ))}
          </div>
        )}
      </section>

      <section>
        <div className={styles.cardHead}>
          <h3 className={styles.cardTitle}>Recovery / readiness</h3>
          <span className={styles.cardSub}>Agent-computed</span>
        </div>
        {(recovery.data?.signals ?? []).length === 0 ? (
          <div className={styles.empty}>
            No readiness signals yet. A recovery agent (e.g. K-OS Health
            recovery agent) computes these from HRV, soreness, and prior
            load. POST to /api/apps/health/recovery/:date to add one.
          </div>
        ) : (
          <div className={styles.list}>
            {(recovery.data?.signals ?? []).slice(0, 14).map((r) => (
              <div key={r.id} className={styles.row}>
                <div className={styles.rowMain}>
                  <div className={styles.rowTitle}>{r.date}</div>
                  <div className={styles.rowMeta}>
                    HRV {r.hrv ?? '—'} · soreness {r.soreness ?? '—'} · mood{' '}
                    {r.mood ?? '—'}
                    {r.modelVersion ? ` · v${r.modelVersion}` : ''}
                  </div>
                </div>
                {r.readinessScore !== null && (
                  <span className={styles.rowValue}>{r.readinessScore}</span>
                )}
              </div>
            ))}
          </div>
        )}
      </section>

      <SleepFormModal
        open={modalOpen}
        onClose={() => setModalDate(undefined)}
        date={modalDate ?? undefined}
      />
    </div>
  );
}

/**
 * Turn a sleep session list into chart points using a session→value
 * accessor. Sessions without a value are dropped. Dates are anchored at
 * local noon so the X-axis labels show the right day across timezones.
 */
function sessionsToPoints(
  sessions: SleepSessionDto[],
  pick: (s: SleepSessionDto) => number | null,
): ChartPoint[] {
  const points: ChartPoint[] = [];
  for (const s of sessions) {
    const v = pick(s);
    if (v == null || !Number.isFinite(v)) continue;
    // s.date is YYYY-MM-DD. Anchor at local noon for stable tick labels.
    const t = new Date(`${s.date}T12:00:00`).getTime();
    if (!Number.isFinite(t)) continue;
    points.push({ t, v });
  }
  // Sessions arrive newest-first; chart wants ascending.
  points.sort((a, b) => a.t - b.t);
  return points;
}
